import type { DesignedProps, DesignManifestEntry, DesignMeta } from '@sarnia/shared';

/**
 * "Ready to share?" checks for designed templates: things a visitor would notice that the owner has not changed yet,
 * worst first. Pure, so it is cheap to re-run on every edit and easy to test.
 */

export type CheckSeverity = 'high' | 'medium' | 'low';
export type CheckTab = 'content' | 'items' | 'images' | 'links' | 'business';

export interface CheckIssue {
  id: string;
  severity: CheckSeverity;
  title: string;
  detail: string;
  /** A few concrete examples, e.g. the sample phone number, so the owner knows what to look for. */
  examples?: string[];
  /** Where to fix it, and the preview element to point at. */
  tab?: CheckTab;
  focus?: string;
  /** Some things can be fixed for the owner in one click. */
  autoFix?: { label: string; patch: Partial<DesignedProps> };
}

export interface CheckInput {
  props: DesignedProps;
  entry: DesignManifestEntry;
  meta: DesignMeta;
  /** data-ed index -> the template's own text, from the design HTML (see defaultTexts). */
  defaults: Record<string, string>;
  now?: Date;
}

const norm = (value: string) => value.replace(/\s+/g, ' ').trim();

/** Reads each editable text's original wording from the design HTML. */
export function defaultTexts(designHtml: string): Record<string, string> {
  const doc = new DOMParser().parseFromString(designHtml, 'text/html');
  const out: Record<string, string> = {};
  doc.querySelectorAll('[data-ed]').forEach((el) => {
    const id = el.getAttribute('data-ed');
    if (id && /^\d{1,4}$/.test(id)) out[id] = norm(el.textContent ?? '');
  });
  return out;
}

// UK-style phone numbers (01234 567890, +44 1423 000000, 07700 900123), emails and postcodes.
export const PHONE = /(?:\+44\s?\(?0?\)?\s?|\b0)\d{2,4}[\s-]?\d{3,4}[\s-]?\d{3,4}\b/;
export const EMAIL = /[\w.+-]+@[\w-]+\.[\w.-]+/;
const POSTCODE = /\b(?:GY|JE|[A-Z]{1,2})\d[A-Z\d]?\s?\d[A-Z]{2}\b/;
const COPYRIGHT_YEAR = /(©|&copy;|\(c\)|copyright)\s*(\d{4})/i;
const SAMPLE_HREF = /^(tel:|mailto:|sms:|https?:\/\/(?:www\.)?(?:wa\.me|example\.(?:com|org|net)))/i;

/** The template's business name: the part of its title before " | " or " — ". */
export function templateBrand(entry: DesignManifestEntry): string {
  return norm(entry.title.split(/\s[|—–-]\s/)[0] ?? '');
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function clip(text: string, max = 48): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export function runSiteCheck({ props, entry, meta, defaults, now = new Date() }: CheckInput): CheckIssue[] {
  const issues: CheckIssue[] = [];
  const texts = props.texts ?? {};
  const links = props.links ?? {};
  const images = props.images ?? {};
  const textIds = Object.keys(defaults);
  const current = (id: string) => (texts[id] !== undefined ? norm(texts[id]) : defaults[id] ?? '');
  const untouched = textIds.filter((id) => texts[id] === undefined || norm(texts[id]) === defaults[id]);

  // 1. The template's own business name still showing.
  const brand = templateBrand(entry);
  if (brand.length >= 3 && !norm(meta.title).toLowerCase().includes(brand.toLowerCase())) {
    const hits = textIds.filter((id) => current(id).toLowerCase().includes(brand.toLowerCase()));
    if (hits.length > 0) {
      issues.push({
        id: 'brand',
        severity: 'high',
        title: `"${brand}" still appears ${plural(hits.length, 'time')}`,
        detail: "That's the template's made-up business name. Click it in the preview and type yours.",
        tab: 'content',
        focus: `[data-ed="${hits[0]}"]`
      });
    }
  }

  // 2. Sample phone numbers, emails and addresses in text the owner has not touched.
  const contactHits = untouched
    .map((id) => {
      const text = defaults[id] ?? '';
      const match = text.match(PHONE) ?? text.match(EMAIL) ?? text.match(POSTCODE);
      return match ? { id, text } : null;
    })
    .filter((hit): hit is { id: string; text: string } => hit !== null);
  if (contactHits.length > 0) {
    issues.push({
      id: 'contact-text',
      severity: 'high',
      title: 'Sample address or phone number on the page',
      detail: 'Visitors would call or visit the wrong place. Click each one in the preview to change it.',
      examples: contactHits.slice(0, 3).map((hit) => clip(hit.text)),
      tab: 'content',
      focus: `[data-ed="${contactHits[0]!.id}"]`
    });
  }

  // 3. Phone, email and social links still pointing at the template's samples.
  const sampleLinks = entry.links
    .map((link, index) => ({ ...link, index }))
    .filter((link) => links[String(link.index)] === undefined && SAMPLE_HREF.test(link.href));
  if (sampleLinks.length > 0) {
    issues.push({
      id: 'contact-links',
      severity: 'high',
      title: `${plural(sampleLinks.length, 'button')} still ${sampleLinks.length === 1 ? 'goes' : 'go'} to sample contact details`,
      detail: 'Call, email and social buttons go to made-up numbers and example.com until you set them.',
      examples: sampleLinks.slice(0, 3).map((link) => `${link.label}: ${link.href.replace(/^(tel|mailto|sms):/, '')}`),
      tab: 'links',
      focus: `[data-lnk="${sampleLinks[0]!.index}"]`
    });
  }

  // 4. Buttons that go nowhere.
  const deadLinks = entry.links
    .map((link, index) => ({ ...link, index, href: links[String(index)] ?? link.href }))
    .filter((link) => link.href.trim() === '#' || link.href.trim() === '');
  if (deadLinks.length > 0) {
    issues.push({
      id: 'dead-links',
      severity: 'medium',
      title: `${plural(deadLinks.length, 'button')} ${deadLinks.length === 1 ? "doesn't" : "don't"} go anywhere`,
      detail: 'Point them at a page, a section, a phone number or an email address.',
      examples: deadLinks.slice(0, 3).map((link) => link.label),
      tab: 'links',
      focus: `[data-lnk="${deadLinks[0]!.index}"]`
    });
  }

  // 5. Example products, menus and prices.
  const untouchedLists = (entry.data ?? []).filter(
    (list) => list.kind === 'array' && list.items.length > 0 && !Array.isArray(props.data?.[list.name])
  );
  if (untouchedLists.length > 0) {
    const count = untouchedLists.reduce((sum, list) => sum + (list.kind === 'array' ? list.items.length : 0), 0);
    issues.push({
      id: 'lists',
      severity: 'medium',
      title: `${plural(count, 'example item')} with sample prices`,
      detail: 'The products, menu or listings are still the template’s examples. Edit, remove or add your own in Items.',
      tab: 'items'
    });
  }

  // 6. Logo and pictures.
  const logos = entry.images
    .map((image, index) => ({ ...image, index }))
    .filter((image) => /logo/i.test(image.label ?? '') && !images[String(image.index)]);
  if (logos.length > 0) {
    issues.push({
      id: 'logo',
      severity: 'medium',
      title: 'Add your logo',
      detail: 'The header still shows the template’s logo.',
      tab: 'images',
      focus: `[data-img="${logos[0]!.index}"]`
    });
  }
  const photoSlots = entry.images.map((image, index) => ({ ...image, index })).filter((image) => image.kind === 'box');
  const samplePhotos = photoSlots.filter((image) => !images[String(image.index)]);
  if (samplePhotos.length > photoSlots.length / 2) {
    issues.push({
      id: 'photos',
      severity: 'low',
      title: `${plural(samplePhotos.length, 'picture')} still the template's`,
      detail: 'Your own photos of the place, the team or the work make the biggest difference to how trustworthy the page feels.',
      tab: 'images',
      focus: `[data-img="${samplePhotos[0]!.index}"]`
    });
  }
  const noAlt = Object.entries(images).filter(([, image]) => image && !norm(image.alt ?? ''));
  if (noAlt.length > 0) {
    issues.push({
      id: 'alt',
      severity: 'low',
      title: `Describe ${plural(noAlt.length, 'picture')}`,
      detail: 'A few words per picture help people using screen readers, and help Google understand the page.',
      tab: 'images',
      focus: `[data-img="${noAlt[0]![0]}"]`
    });
  }

  // 7. Most of the words are still the template's. Only real copy counts: nobody needs to reword "Home" or "Menu".
  const copyIds = textIds.filter((id) => (defaults[id] ?? '').length >= 25);
  const untouchedCopy = copyIds.filter((id) => untouched.includes(id));
  if (copyIds.length >= 5 && untouchedCopy.length / copyIds.length > 0.6) {
    issues.push({
      id: 'sample-text',
      severity: 'medium',
      title: `${Math.round((untouchedCopy.length / copyIds.length) * 100)}% of the writing is the template's`,
      detail: 'Headlines and the intro matter most. Click any text in the preview to make it yours.',
      tab: 'content'
    });
  }

  // 8. Out-of-date copyright year: fixable in one click.
  const year = now.getFullYear();
  const staleYears = textIds
    .map((id) => ({ id, text: current(id), match: current(id).match(COPYRIGHT_YEAR) }))
    .filter((hit) => hit.match && Number(hit.match[2]) < year && Number(hit.match[2]) > 1990);
  if (staleYears.length > 0) {
    const nextTexts = { ...texts };
    for (const hit of staleYears) nextTexts[hit.id] = hit.text.replace(COPYRIGHT_YEAR, (_all, sign: string) => `${sign} ${year}`);
    issues.push({
      id: 'year',
      severity: 'low',
      title: `Footer says ${staleYears[0]!.match![1]} ${staleYears[0]!.match![2]}`,
      detail: `Update it to ${year} so the page doesn't look abandoned.`,
      focus: `[data-ed="${staleYears[0]!.id}"]`,
      autoFix: { label: `Change to ${year}`, patch: { texts: nextTexts } }
    });
  }

  // 9. Search and browser details.
  const description = norm(meta.description);
  if (!description) {
    issues.push({
      id: 'description',
      severity: 'medium',
      title: 'Add a description',
      detail: 'One or two sentences about what you do and where. Google shows it under your name in search results.',
      tab: 'content'
    });
  } else if (description === norm(entry.description)) {
    issues.push({
      id: 'description',
      severity: 'medium',
      title: "The description is the template's",
      detail: 'Google shows it under your name in search results. Say what you do and where.',
      tab: 'content'
    });
  }
  const business = props.business;
  if (!business || (!business.phone && !business.street && !business.town && !business.hours)) {
    issues.push({
      id: 'business',
      severity: 'medium',
      title: 'Tell Google where and when',
      detail: 'Add your address, phone and opening hours in the Business tab so you can show up in local searches and on maps.',
      tab: 'business'
    });
  }
  if (!props.favicon) {
    issues.push({
      id: 'favicon',
      severity: 'low',
      title: 'No site icon',
      detail: 'The small picture in browser tabs. Your logo works well.',
      tab: 'content'
    });
  }

  return sortIssues(issues);
}

function sortIssues(issues: CheckIssue[]): CheckIssue[] {
  const order: Record<CheckSeverity, number> = { high: 0, medium: 1, low: 2 };
  return issues.sort((a, b) => order[a.severity] - order[b.severity]);
}
